import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import createOrderApi, { OrderParams } from 'api/order';
import { currentUserSelector, metadatasSelector } from 'modules/user/userSlice';
import { AppThunk, RootState } from 'store';
import { syncAttribution } from 'modules/user/attributionSlice';
import { appContextSelector } from 'modules/app/appSlice';
import { showToast } from 'modules/app/toasterSlice';
import { singlePorfolioSanityIdSelector, virtualPortfoliosSelector } from './portfolioSelectSlice';
import { omit } from 'lodash-es';
import { getDisplayName } from 'utils/metadata';
import { loadBills } from 'modules/billing/billingSlice';
import { getOrderStatus } from 'pages/shared/CorporateOffset/helpers/getOrderStatus';
import { setTag, tags } from 'utils/tags';
import { fixFloat, roundNumberToTwo } from 'utils/numberFormatter';
import { transactionProductsName } from 'utils/order';
import { validateItemPortfolio } from 'pages/shared/CorporateOffset/helpers/validateItemPortfolio';

interface OffsetInfo {
  orderName?: string;
  country?: Option;
  department?: Option;
  description?: string;
  emissionScope?: Option;
  timeFrameFrom?: Date;
  timeFrameTo?: Date;
  isPaidByPartner?: boolean;
  [key: string]: string | Option | Date | undefined | boolean | number;
}

interface OrderPayload {
  number: string;
  date: string;
  name?: string;
  id: string;
  claimCode?: string;
  inviteId?: string;
  billId?: string;
  orderStatus?: string;
}

export interface CarbonOffsetState {
  amount?: number;
  totalKilosCo2?: number;
  vat?: number;
  customerVat?: number;
  excise?: number;
  customerExcise?: number;
  orderName?: string;
  orderId?: string;
  country?: Option;
  department?: Option;
  description?: string;
  emissionScope?: Option;
  timeFrameFrom?: Date;
  timeFrameTo?: Date;
  isLoading: boolean;
  customerPrice?: number;
  price?: number;
  currency?: string;
  orderNumber?: string;
  orderDate?: string;
  selectedPortfolioId?: string;
  isOrderInfoInvalid?: boolean;
  selectedCustomMetadata?: string[];
  isPaidByPartner?: boolean;
  customer?: PartnersCustomerOption;
  claimCode?: string;
  inviteId?: string;
  billId?: string;
  isMultiplierActive?: boolean;
  orderStatus?: string;
  isMultiUpload?: undefined;
  attachmentTempUrl?: undefined;
  fullOffsetRows?: undefined;
  costCenter?: string;
  poNumber?: string;
  totalKilosCo2Avoided?: number;
  totalKilosCo2Offset?: number;
  [key: string]: string | Option | Date | number | undefined | boolean | string[] | PartnersCustomerOption;
}

const initialState: CarbonOffsetState = {
  isLoading: false,
  isOrderInfoInvalid: true,
};

const slice = createSlice({
  name: 'offsetCarbon',
  initialState,
  reducers: {
    reset: (): CarbonOffsetState => {
      return { ...initialState };
    },
    resetWithoutPortfolioId: (state: CarbonOffsetState): CarbonOffsetState => {
      return { ...initialState, selectedPortfolioId: state.selectedPortfolioId };
    },
    setLoading: (state: CarbonOffsetState, action: PayloadAction<boolean>): CarbonOffsetState => {
      state.isLoading = action.payload;
      return state;
    },
    setIsMultiplierActive: (state: CarbonOffsetState, action: PayloadAction<boolean>): CarbonOffsetState => {
      state.isMultiplierActive = action.payload;
      return state;
    },
    setOrder: (state: CarbonOffsetState, action: PayloadAction<OrderPayload>): CarbonOffsetState => {
      state.orderNumber = action.payload.number;
      state.orderDate = action.payload.date;
      state.orderName = action.payload.name ?? '';
      state.orderId = action.payload.id;
      state.inviteId = action.payload.inviteId;
      state.claimCode = action.payload.claimCode;
      state.billId = action.payload.billId;
      state.orderStatus = action.payload.orderStatus;
      return state;
    },
    setAmount: (state: CarbonOffsetState, action: PayloadAction<number>): CarbonOffsetState => {
      state.amount = action.payload;
      return state;
    },
    setOrderInfo: (state: CarbonOffsetState, action: PayloadAction<OffsetInfo>): CarbonOffsetState => {
      Object.keys(action.payload).map(key => {
        state[key] = action.payload[key];
      });
      return state;
    },
    setOrderInfoInvalid: (state: CarbonOffsetState, action: PayloadAction<boolean>): CarbonOffsetState => {
      state.isOrderInfoInvalid = action.payload;
      return state;
    },
    setSelectedCustomMetadata: (state: CarbonOffsetState, action: PayloadAction<string[]>): CarbonOffsetState => {
      state.selectedCustomMetadata = action.payload;
      return state;
    },
    setOffsetPrice: (
      state: CarbonOffsetState,
      action: PayloadAction<{ price: number; vat: number; customerPrice: number; customerVat: number; excise: number; customerExcise: number }>,
    ): CarbonOffsetState => {
      state.price = action.payload.price;
      state.vat = action.payload.vat;
      state.excise = action.payload.excise;
      state.customerPrice = action.payload.customerPrice;
      state.customerVat = action.payload.customerVat;
      state.customerExcise = action.payload.customerExcise;
      return state;
    },
    setEmissionsAvoided: (state: CarbonOffsetState, action: PayloadAction<number>): CarbonOffsetState => {
      state.totalKilosCo2Avoided = action.payload;
      return state;
    },
    setEmissionsOffset: (state: CarbonOffsetState, action: PayloadAction<number>): CarbonOffsetState => {
      state.totalKilosCo2Offset = action.payload;
      return state;
    },
  },
});

const {
  reset,
  setLoading,
  setOrder,
  setAmount,
  setOrderInfo,
  resetWithoutPortfolioId,
  setOrderInfoInvalid,
  setSelectedCustomMetadata,
  setIsMultiplierActive,
  setOffsetPrice,
  setEmissionsAvoided,
  setEmissionsOffset,
} = slice.actions;
export { reset, setOrderInfo, setOrderInfoInvalid, resetWithoutPortfolioId, setSelectedCustomMetadata, setAmount, setIsMultiplierActive };
export default slice.reducer;

export const getCarbonOrderObject = ({
  offsetProps,
  currentUser,
  virtualPortfolios,
  mainPortfolioId,
  userCurrency,
  customer,
}: {
  offsetProps: CarbonOffsetState;
  currentUser: Account | undefined;
  virtualPortfolios: {
    portfolioId: string;
    weight: number;
  }[];
  mainPortfolioId: string | undefined;
  isMultiUpload?: boolean;
  userCurrency: string;
  customer: PartnersCustomerOption | undefined;
}): OrderParams => {
  const { isMultiplierActive, amount } = offsetProps;
  const tonnesFactor = 1000;

  const totalKilosCo2 = Number(amount ?? 0) * tonnesFactor;
  const portfolioId = mainPortfolioId ?? null;

  const multiplierMode = currentUser?.partnership?.settings?.portal?.multiplier?.mode;
  const isDiscountMode = multiplierMode === 'Discount';
  const multiplierValue = currentUser?.partnership?.settings?.portal?.multiplier?.value ?? 0;
  const discountValue = multiplierValue / (1 + multiplierValue);
  const discountedOffset = isMultiplierActive && multiplierValue ? (totalKilosCo2 ?? 0) * discountValue : 0;
  const mappedVirtualPortfolios = virtualPortfolios.map(({ portfolioId, weight }) => ({
    id: portfolioId,
    weight: isMultiplierActive && isDiscountMode ? weight * discountValue : weight,
  }));

  const order = {
    currency: userCurrency,
    category: 'Carbon',
    items: [
      {
        type: 'impact',
        impact: 'Co2',
        kilosCo2: isDiscountMode ? totalKilosCo2 - discountedOffset : totalKilosCo2,
        portfolio: mainPortfolioId
          ? undefined
          : {
              type: 'Virtual',
              impact: 'co2',
              currency: userCurrency,
              childPortfolios: mappedVirtualPortfolios,
            },
        portfolioId,
        feeFactors: {
          bulkCo2: true,
          percentageBulkCo2: true,
        },
        taxFactors: {
          customerCountry: customer?.country,
          customerCountrySubdivision: customer?.countrySubdivision,
        },
      },
    ],
  };

  return order;
};

export const getPriceForOrder = (): AppThunk => async (dispatch, getState): Promise<void> => {
  const carbonOffset = getState().carbonOffset;
  const { customer, currency } = carbonOffset;
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const virtualPortfolios = virtualPortfoliosSelector(getState());
  const mainPortfolioId = singlePorfolioSanityIdSelector(getState());
  const orderApi = createOrderApi(context);
  const userCurrency = currency ?? 'USD';
  const customerCurrency = customer?.currency ?? 'USD';
  const customerId = currentUser?.customerId ?? '';
  const partnershipId = currentUser?.partnershipId ?? '';
  const isConnectContext = context === 'connect';

  dispatch(setLoading(true));
  try {
    const order = getCarbonOrderObject({ offsetProps: carbonOffset, currentUser, virtualPortfolios, mainPortfolioId, userCurrency, customer });

    validateItemPortfolio(order.items?.[0]);

    const response =
      context === 'connect' ? await orderApi.getQuoteForPartner(order, partnershipId) : await orderApi.getQuoteForCustomer(order, customerId);

    let customerPrice = response.totalPrice;
    let customerVat = response.totalVat;
    let customerExcise = response.totalExcise;
    let emissionsAvoided = response.totalKilosCo2Avoided;
    let emissionsOffset = response.totalKilosCo2Offset;
    if (isConnectContext && customer && customerCurrency !== userCurrency) {
      const customerOrder = getCarbonOrderObject({
        offsetProps: carbonOffset,
        currentUser,
        virtualPortfolios,
        mainPortfolioId,
        userCurrency: customerCurrency,
        customer,
      });
      const customerResponse = await orderApi.getQuoteForPartner(customerOrder, partnershipId);
      customerPrice = customerResponse.totalPrice;
      customerVat = customerResponse.totalVat;
      customerExcise = customerResponse.totalExcise;
      emissionsAvoided = customerResponse.totalKilosCo2Avoided;
      emissionsOffset = customerResponse.totalKilosCo2Offset;
    }

    dispatch(
      setOffsetPrice({
        price: response.totalPrice,
        vat: response.totalVat,
        excise: response.totalExcise,
        customerPrice,
        customerVat,
        customerExcise,
      }),
    );

    dispatch(setEmissionsAvoided(emissionsAvoided ?? 0));
    dispatch(setEmissionsOffset(emissionsOffset ?? 0));
  } catch (err) {
    //@ts-ignore
    if (err.data?.includes('Insufficient supply')) {
      dispatch(
        showToast({
          variant: 'error',
          titlei18nKey: 'offset:corporate.portfolios.errorTitle',
          descriptioni18nKey: 'offset:corporate.portfolios.insufficientSupplyErrorMessage',
        }),
      );
      //@ts-ignore
    } else if (err.data) {
      dispatch(showToast({ variant: 'error', titlei18nKey: 'offset:errorTitle', descriptioni18nKey: 'offset:fetchPriceError' }));
    }
  } finally {
    dispatch(setLoading(false));
  }
};

export const createCarbonOrder = (errorCb?: CallableFunction, successCb?: CallableFunction): AppThunk => async (
  dispatch,
  getState,
): Promise<void> => {
  dispatch(setLoading(true));
  const context = appContextSelector(getState());
  const virtualPortfolios = virtualPortfoliosSelector(getState());
  const mainPortfolioId = singlePorfolioSanityIdSelector(getState());
  const metadata = metadatasSelector(getState()) ?? [];
  const currentUser = currentUserSelector(getState());
  const portfolioId = mainPortfolioId ?? null;
  const orderApi = createOrderApi(context);
  const tonnesFactor = 1000;
  const {
    amount,
    currency,
    orderName,
    description,
    isPaidByPartner,
    customer,
    isMultiplierActive,
    price,
    customerPrice,
    poNumber,
    costCenter,
    ...restProps
  } = getState().carbonOffset;
  const { carbonOffset, user } = getState();
  const totalKilosCo2 = Number(amount ?? 0) * tonnesFactor;
  const customMetadata: {
    internalName: string;
    displayName: string;
    value: string | Date;
  }[] = [];
  const orderCurrency = !isPaidByPartner && customer?.currency ? customer.currency ?? '' : currency ?? '';
  const multiplierValue = currentUser?.partnership?.settings?.portal?.multiplier?.value ?? 0;
  const multiplierMode = currentUser?.partnership?.settings?.portal?.multiplier?.mode;
  const isDiscountMode = multiplierMode === 'Discount';
  const discountValue = multiplierValue / (1 + multiplierValue);
  const discountedOffset = isMultiplierActive && multiplierValue ? (totalKilosCo2 ?? 0) * discountValue : 0;

  const customMetadatas = {
    ...omit(restProps, [
      'isOrderInfoInvalid',
      'isLoading',
      'vat',
      'customerVat',
      'excise',
      'customerExcise',
      'price',
      'selectedCustomMetadata',
      'claimCode',
      'inviteId',
      'totalKilosCo2Avoided',
      'totalKilosCo2Offset',
    ]),
  };

  Object.keys(customMetadatas).map(key => {
    let value: string | Date = '';

    if ((carbonOffset[key] as Option)?.value) {
      value = (carbonOffset[key] as Option)?.value;
    } else {
      value = carbonOffset[key] as string | Date;
    }
    if (value) {
      customMetadata.push({
        internalName: key,
        value: value,
        displayName: getDisplayName(metadata, key),
      });
    }
  });

  const order = {
    currency: orderCurrency,
    comment: description ?? '',
    description: orderName ?? '',
    category: 'Carbon',
    items: [
      {
        type: 'impact',
        impact: 'Co2',
        kilosCo2: isDiscountMode ? totalKilosCo2 - discountedOffset : totalKilosCo2,
        ...(mainPortfolioId ? {} : { virtualPortfolios }),
        price: isPaidByPartner || !customer ? price ?? 0 : customerPrice ?? 0,
        portfolioId,
        feeFactors: {
          bulkCo2: true,
          percentageBulkCo2: true,
        },
        taxFactors: {
          customerCountry: customer?.country,
          customerCountrySubdivision: customer?.countrySubdivision,
        },
      },
    ],
    metadata: {
      order: {
        channel: 'portal-fulloffset',
      },
      custom: customMetadata,
    },
    multiplier: isMultiplierActive ? { value: multiplierValue } : undefined,
  };

  try {
    const orderResult =
      context === 'connect'
        ? await orderApi.createForPartner(
            {
              ...order,
              facilitated: { isFacilitated: true, isPaidByPartner: isPaidByPartner ?? true },
              customer: {
                customerId: customer?.value ?? '',
              },
              costCenter: costCenter ?? '',
              poNumber: poNumber ?? '',
            },
            user.currentAccount?.partnershipId ?? '',
          )
        : await orderApi.create(order, user.currentAccount?.customerId ?? '');
    if (context !== 'connect' || isPaidByPartner) {
      setTag({
        ...tags.portalOffset.placeOrder,
        categories: [orderResult.order.category],
        transactionId: orderResult.orderId,
        transactionTotal: orderResult.order.totalPrice,
        currencyCode: orderResult.order.currency,
        transactionProducts: orderResult.order.items.map(item => ({
          category: orderResult.order.category,
          name: `${transactionProductsName.bulk} offset`,
          price: roundNumberToTwo(item.price),
          quantity: item.kilosCo2,
          sku: `${transactionProductsName.bulk.toLowerCase()}-offset`,
        })),
      });
    }
    dispatch(
      setOrder({
        number: orderResult.order.choooseId,
        date: orderResult.order.createdDate,
        name: orderName ?? '',
        id: orderResult.orderId,
        claimCode: orderResult.claimCode,
        inviteId: orderResult.inviteId,
        billId: orderResult.billId,
        orderStatus: getOrderStatus(orderResult.order),
      }),
    );

    dispatch(syncAttribution());
    dispatch(loadBills());
    successCb && successCb();
    dispatch(setLoading(false));
  } catch (err) {
    dispatch(setLoading(false));
    dispatch(showToast({ variant: 'error', titlei18nKey: 'offset:errorTitle', descriptioni18nKey: 'offset:createOrderError' }));
    errorCb && errorCb();
  }
};

export const carbonAmountSelector = createSelector(
  (state: RootState) => state.carbonOffset,
  state => {
    const { amount } = state;

    return fixFloat(amount ?? 0);
  },
);
